import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CTASection from '../../components/CTASection';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Printer, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { cookiePolicy } from '../../data/legalData';

const CookiesPage = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = cookiePolicy.sections.map(s => ({
        id: s.id,
        offset: document.getElementById(s.id)?.offsetTop || 0
      }));
      
      const scrollPos = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        if (scrollPos >= sections[i].offset) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 120;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen" data-testid="cookies-page">
      <Header />
      
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-[rgb(var(--foreground-muted))] mb-8">
            <Link to="/legal" className="hover:text-[rgb(var(--primary))] transition-colors">
              Legal
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Cookie Policy</span>
          </div>

          <div className="flex flex-col lg:flex-row gap-12">
            {/* Table of Contents */}
            <aside className="hidden lg:block lg:w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-[rgb(var(--foreground-muted))] uppercase tracking-wide">
                    Contents
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[rgb(var(--foreground-muted))] hover:text-white print:hidden"
                    onClick={handlePrint}
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                </div>
                <nav className="space-y-1">
                  {cookiePolicy.sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200',
                        activeSection === section.id
                          ? 'bg-[rgb(var(--primary))]/10 text-[rgb(var(--primary))] font-medium'
                          : 'text-[rgb(var(--foreground-muted))] hover:bg-white/5 hover:text-white'
                      )}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <div className="mb-10">
                <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                  Cookie Policy
                </h1>
                <p className="text-[rgb(var(--foreground-muted))]">
                  Last updated: {cookiePolicy.lastUpdated}
                </p>
              </div>

              <div className="space-y-12 legal-content">
                {cookiePolicy.sections.map((section) => (
                  <section key={section.id} id={section.id} className="scroll-mt-32">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold mb-4">
                      {section.title}
                    </h2>
                    <div className="prose prose-invert max-w-none text-[rgb(var(--foreground-muted))] leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-white/10">
                <Link to="/legal">
                  <Button variant="outline" className="rounded-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Legal
                  </Button>
                </Link>
              </div>
            </main>
          </div>
        </div>
      </section>

      <CTASection />

      <Footer />
    </div>
  );
};

export default CookiesPage;
